/**
 * ListViewModeToggle — przełącznik trybu widoku (Lista / Karty / Kanban)
 * do osadzenia w nagłówku strony listy, w jednym rzędzie z „Operacje /
 * Kreator / Dodaj" (parytet z modułami custom i SellCall).
 *
 * Komponent jest „głupi" — trzyma stan strona (tryb sterowany), a tabela
 * (`ConfigurableTable`) dostaje ten sam `viewMode` przez prop i sama zapisuje
 * `defaultViewMode` do layoutu.
 *
 * Domyślnie pokazuje trzy tryby kartotek (Lista / Karty / Kanban). Strony list
 * silnika NIE podają zestawu ręcznie — biorą go z hooka `useListViewMode`, który
 * dostaje od tabeli tryby dostępne w bieżącym layoucie (m.in. Kalendarz tylko przy
 * skonfigurowanym `calendarConfig`) i mapuje je na pastylki przez `listViewModeOptions`.
 * Kartoteki modułowe mają szerszy zestaw (Lista / Galeria / Kalendarz / Kanban) o innych
 * kodach trybu — podają własną listę przez `modes`, zachowując TEN SAM wygląd
 * pastylki. Dzięki temu jeden komponent obsługuje oba światy bez rozjazdu stylu.
 */

import { useTranslation } from 'react-i18next';
import type { ViewMode } from '@saas/shared';
import {
  CalendarDays,
  KanbanSquare,
  LayoutGrid,
  List as ListIcon,
  type LucideIcon,
} from 'lucide-react';

export interface ViewModeOption<M extends string = ViewMode> {
  mode: M;
  Icon: LucideIcon;
  /** Gotowa (już przetłumaczona) etykieta tooltipa. Gdy pominięta — klucz i18n trybu. */
  title?: string;
}

/** Ikona pastylki per tryb silnika list — `Record` wymusza pokrycie każdego `ViewMode`. */
const VIEW_MODE_ICONS: Record<ViewMode, LucideIcon> = {
  list: ListIcon,
  cards: LayoutGrid,
  kanban: KanbanSquare,
  calendar: CalendarDays,
};

/** Tryby kartotek bez wiedzy o layoucie (kalendarz dochodzi dopiero z `calendarConfig`). */
export const DEFAULT_LIST_VIEW_MODES: readonly ViewMode[] = ['list', 'cards', 'kanban'];

/** Pastylki dla podanych trybów silnika list, w kolejności wejścia (etykieta z i18n trybu). */
export function listViewModeOptions(modes: readonly ViewMode[]): ViewModeOption[] {
  return modes.map((mode) => ({ mode, Icon: VIEW_MODE_ICONS[mode] }));
}

const DEFAULT_MODES: ViewModeOption[] = listViewModeOptions(DEFAULT_LIST_VIEW_MODES);

interface ListViewModeToggleProps<M extends string = ViewMode> {
  value: M;
  onChange: (mode: M) => void;
  /** Zestaw trybów do pokazania. Domyślnie Lista / Karty / Kanban (parytet kartotek). */
  modes?: ViewModeOption<M>[];
  testIdPrefix?: string;
  /** Nadpisanie klas kontenera (np. `h-10 !rounded-full` w nagłówku kartoteki wg Figmy). */
  className?: string;
}

export function ListViewModeToggle<M extends string = ViewMode>({
  value,
  onChange,
  modes,
  testIdPrefix,
  className,
}: ListViewModeToggleProps<M>) {
  const { t } = useTranslation();
  const options = modes ?? (DEFAULT_MODES as ViewModeOption<M>[]);

  return (
    <div
      className={`flex h-11 items-center gap-0.5 rounded-full border border-border-secondary bg-bg-elevated px-1 shadow-[0_1px_2px_rgba(23,23,33,0.06)] ${className ?? ''}`}
      role="group"
      data-testid={testIdPrefix ? `${testIdPrefix}-viewmode` : undefined}
    >
      {options.map(({ mode, Icon, title }) => {
        const active = value === mode;
        const label = title ?? t(`tenant.listBuilder.viewMode.${mode}`);
        return (
          <button
            key={mode}
            type="button"
            onClick={() => onChange(mode)}
            aria-pressed={active}
            aria-label={label}
            title={label}
            data-testid={testIdPrefix ? `${testIdPrefix}-viewmode-${mode}` : undefined}
            className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
              active
                ? 'bg-bg-tertiary text-text-primary'
                : 'text-text-muted hover:text-text-primary'
            }`}
          >
            <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}
